const DEFAULT_QUEUE_LIMIT = 8;
const DEFAULT_STALL_TIMEOUT_MS = 30_000;

type ProgressWaiter = {
  resolve: () => void;
  reject: (error: Error) => void;
  cleanup: () => void;
};

type FailureWaiter = {
  reject: (error: Error) => void;
  cleanup: () => void;
};

export class EncoderBackpressureGate {
  private failure: Error | null = null;
  private progressWaiters = new Set<ProgressWaiter>();
  private failureWaiters = new Set<FailureWaiter>();

  constructor(
    private readonly getQueueSize: () => number,
    private readonly queueLimit = DEFAULT_QUEUE_LIMIT,
    private readonly stallTimeoutMs = DEFAULT_STALL_TIMEOUT_MS
  ) {}

  notifyOutput(): void {
    const waiters = Array.from(this.progressWaiters);
    this.progressWaiters.clear();
    waiters.forEach((waiter) => {
      waiter.cleanup();
      waiter.resolve();
    });
  }

  fail(error: unknown): void {
    if (this.failure) {
      return;
    }
    this.failure = error instanceof Error ? error : new Error(String(error));
    const waiters = Array.from(this.progressWaiters);
    this.progressWaiters.clear();
    waiters.forEach((waiter) => {
      waiter.cleanup();
      waiter.reject(this.failure!);
    });
    const failureWaiters = Array.from(this.failureWaiters);
    this.failureWaiters.clear();
    failureWaiters.forEach((waiter) => {
      waiter.cleanup();
      waiter.reject(this.failure!);
    });
  }

  async waitForCapacity(signal?: AbortSignal): Promise<void> {
    this.throwIfUnavailable(signal);
    while (this.getQueueSize() > this.queueLimit) {
      await this.waitForProgress(signal);
      this.throwIfUnavailable(signal);
    }
  }

  async waitForOperation<T>(
    operation: Promise<T>,
    signal: AbortSignal | undefined,
    label: string
  ): Promise<T> {
    this.throwIfUnavailable(signal);

    let cancelControl = () => {};
    const control = new Promise<never>((_, reject) => {
      const fail = (error: Error) => reject(error);
      const timeoutId = window.setTimeout(
        () => fail(new Error(`${label} timed out while waiting for the video encoder.`)),
        this.stallTimeoutMs
      );
      const onAbort = () => fail(getRenderAbortError(signal));
      signal?.addEventListener('abort', onAbort, { once: true });
      const waiter: FailureWaiter = {
        reject: fail,
        cleanup: () => {
          window.clearTimeout(timeoutId);
          signal?.removeEventListener('abort', onAbort);
        }
      };
      this.failureWaiters.add(waiter);
      cancelControl = () => {
        this.failureWaiters.delete(waiter);
        waiter.cleanup();
      };
      if (this.failure) {
        fail(this.failure);
      }
    });

    try {
      return await Promise.race([operation, control]);
    } finally {
      cancelControl();
    }
  }

  throwIfUnavailable(signal?: AbortSignal): void {
    if (signal?.aborted) {
      throw getRenderAbortError(signal);
    }
    if (this.failure) {
      throw this.failure;
    }
  }

  private waitForProgress(signal?: AbortSignal): Promise<void> {
    return new Promise((resolve, reject) => {
      const timeoutId = window.setTimeout(
        () => finishReject(new Error('Video encoder stopped responding.')),
        this.stallTimeoutMs
      );
      const onAbort = () => finishReject(getRenderAbortError(signal));
      const cleanup = () => {
        window.clearTimeout(timeoutId);
        signal?.removeEventListener('abort', onAbort);
      };
      const waiter: ProgressWaiter = {
        resolve: () => {
          cleanup();
          resolve();
        },
        reject: (error) => {
          cleanup();
          reject(error);
        },
        cleanup
      };
      const finishReject = (error: Error) => {
        this.progressWaiters.delete(waiter);
        waiter.reject(error);
      };

      signal?.addEventListener('abort', onAbort, { once: true });
      this.progressWaiters.add(waiter);
      if (this.failure) {
        finishReject(this.failure);
      }
    });
  }
}
import { getRenderAbortError } from './RenderTask';
